import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MerchantStorefront } from '../shop/MerchantStorefront';
import { ShopConfig } from '../../data/shopConfig';
export type PreviewDevice = 'desktop' | 'mobile';
export type PreviewZoom = 'fit' | '75' | '100';
/** Reference viewports. The storefront always renders at these widths. */
const VIEWPORT_WIDTH: Record<PreviewDevice, number> = {
  desktop: 1440,
  mobile: 390
};
const BLANK_DOC = '<!DOCTYPE html><html><head><meta charset="utf-8"></head><body></body></html>';
interface StorefrontViewportProps {
  config: ShopConfig;
  device: PreviewDevice;
  zoom?: PreviewZoom;
  className?: string;
  style?: React.CSSProperties;
}
/**
 * A real browser viewport for the real storefront. The shop is mounted inside
 * an iframe sized to a genuine desktop or mobile viewport, so its production
 * responsive rules apply exactly as they do on the public page, then the whole
 * frame is scaled proportionally to fit the panel — like zooming out in a
 * browser rather than reflowing the layout.
 */
export function StorefrontViewport({
  config,
  device,
  zoom = 'fit',
  className = '',
  style
}: StorefrontViewportProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [box, setBox] = useState({
    w: 0,
    h: 0
  });
  const [frameBody, setFrameBody] = useState<HTMLElement | null>(null);
  useEffect(() => {
    const el = hostRef.current;
    if (!el) return;
    // Only commit real size changes. Writing the same numbers back would make
    // the observer, the scaled frame and the layout chase each other, which
    // reads as the preview shaking.
    let frame = 0;
    const measure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const w = Math.round(el.clientWidth);
        const h = Math.round(el.clientHeight);
        setBox(b => b.w === w && b.h === h ? b : {
          w,
          h
        });
      });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, []);
  // Mirror the app's stylesheets into the frame so the shop is styled by the
  // exact same CSS as the public page — never a preview-only stylesheet.
  const adoptStyles = () => {
    const doc = frameRef.current?.contentDocument;
    if (!doc) return;
    doc.head.innerHTML = '<meta charset="utf-8">';
    document.querySelectorAll('style, link[rel="stylesheet"]').forEach(node => doc.head.appendChild(node.cloneNode(true)));
    doc.documentElement.style.background = '#ffffff';
    doc.body.style.margin = '0';
    setFrameBody(doc.body);
  };
  const viewportWidth = VIEWPORT_WIDTH[device];
  const fitScale = box.w > 0 ? Math.min(1, box.w / viewportWidth) : 1;
  const scale = zoom === 'fit' ? fitScale : zoom === '75' ? 0.75 : 1;
  const frameHeight = box.h > 0 ? box.h / scale : 900;
  return <div ref={hostRef} style={style} className={`relative overflow-hidden bg-white ${className}`} data-id="element-4473" data-mp="2l5wss9zaiotg">
      <div className="absolute inset-0 flex justify-center" data-id="element-4474" data-mp="2p6qecwp9frj6">
        <div className="relative" style={{
        width: viewportWidth * scale,
        height: box.h || undefined
      }} data-id="element-4475" data-mp="2p55kl9arscmd">
          <iframe ref={frameRef} title={`${config.publicName} storefront preview`} srcDoc={BLANK_DOC} onLoad={adoptStyles} style={{
          width: viewportWidth,
          height: frameHeight,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          border: 0,
          display: 'block'
        }} data-id="element-4476" data-mp="2ywthhpq3ilv8" />
          {frameBody && createPortal(<MerchantStorefront config={config} data-id="element-4477" data-mp="3mnp6kf3632ys" />, frameBody)}
        </div>
      </div>
    </div>;
}